/** Loan-specific notice payments. Shared by the browser, Edge and office verifier. */
import { GoodbyeLetterPrereqError } from './goodbye-letter-types.ts';

export const GOODBYE_PAYMENT_VERSION = 'closing-payment-v1';
export const GOODBYE_PAYMENT_PATHS: Record<string, string[]> = {
  '5': ['principalAndInterestMonthlyPaymentAmount'],
  '420': ['loanProductData', 'lienPriorityType'],
  '425': ['buydownIndicator'],
  '4645': ['temporaryBuydown'],
  '608': ['loanAmortizationType'],
  '682': ['loanProductData', 'scheduledFirstPaymentDate'],
  '912': ['proposedHousingExpenseTotal'],
  'CD1.X7': ['closingCost', 'closingDisclosure1', 'ppc1Year'],
  'CD1.X8': ['closingCost', 'closingDisclosure1', 'ppc1MinimumPiPayment'],
  'CD1.X9': ['closingCost', 'closingDisclosure1', 'ppc1MaximumPiPayment'],
  'CD1.X11': ['closingCost', 'closingDisclosure1', 'ppc1MiAmount'],
  'CD1.X12': ['closingCost', 'closingDisclosure1', 'ppc1EstimatedEscrowAmount'],
  'CD1.X13': ['closingCost', 'closingDisclosure1', 'ppc1MinimumMonthlyPayment'],
  'CD1.X14': ['closingCost', 'closingDisclosure1', 'ppc1MaximumMonthlyPayment'],
  'CD1.XD1': ['closingCost', 'closingDisclosure1', 'rangePaymentIndicatorC1'],
  'CD4.X10': ['closingCost', 'closingDisclosure4', 'monthlyEscrowPayment'],
  'HUD24': ['hud1Es', 'escrowPayment']
};
export const GOODBYE_PAYMENT_FIELD_IDS = Object.keys(GOODBYE_PAYMENT_PATHS);
export type GoodbyePaymentFields = Record<string, unknown>;

const alias = (id: string) => `payment_${id.toLowerCase().replace(/\./g, '_')}`;
export const GOODBYE_PAYMENT_SELECT = GOODBYE_PAYMENT_FIELD_IDS
  .map(id => `${alias(id)}:raw_data->${GOODBYE_PAYMENT_PATHS[id].join('->')}`).join(', ');

export function paymentFieldsFromRow(row: Record<string, unknown>): GoodbyePaymentFields {
  return Object.fromEntries(GOODBYE_PAYMENT_FIELD_IDS.map(id => [id, row[alias(id)] ?? null]));
}

export function paymentFieldsFromRaw(raw: Record<string, unknown>): GoodbyePaymentFields {
  return Object.fromEntries(GOODBYE_PAYMENT_FIELD_IDS.map(id => {
    let value: unknown = raw;
    for (const part of GOODBYE_PAYMENT_PATHS[id]) {
      value = value && typeof value === 'object' ? (value as Record<string, unknown>)[part] : null;
    }
    return [id, value ?? null];
  }));
}

const blank = (value: unknown) => value == null || (typeof value === 'string' && value.trim() === '');

/** No rounding of ambiguous values and no blank-to-zero conversion. */
export function paymentCents(value: unknown): number | null {
  if (blank(value)) return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const text = String(value).trim();
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.replace(/,/g, '').split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

export function paymentDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  const iso = us ? `${us[3]}-${us[1]}-${us[2]}` : text;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === iso ? iso : null;
}

function flag(value: unknown): boolean | null {
  if (value === true || value === 'Y' || value === 'Yes' || value === 'true') return true;
  if (value === false || value === 'N' || value === 'No' || value === 'false') return false;
  return null;
}

export interface GoodbyePaymentEvidence {
  version: typeof GOODBYE_PAYMENT_VERSION;
  source: 'CD1.X13';
  amountCents: number;
  principalAndInterestCents: number;
  collectedEscrowCents: number;
  firstPaymentDate: string;
  dueDate: string;
  initialPeriodYears: number;
  lienPosition: 'FirstLien' | 'SecondLien';
  fields: Record<string, string | number | boolean | null>;
}

export function resolveGoodbyePayment(
  fields: GoodbyePaymentFields,
  firstPaymentDate: string,
  dueDate: string,
  scheduleAmount?: unknown
): GoodbyePaymentEvidence {
  const fail = (code: 'PAYMENT_DATA_INCOMPLETE' | 'PAYMENT_DATA_CONFLICT' | 'PAYMENT_PERIOD_UNSUPPORTED', message: string, ids: string[]): never => {
    throw new GoodbyeLetterPrereqError(code, `${message} Review the payment in Encompass and the closing documents, synchronize the loan, and retry.`, ids);
  };
  const money: Record<string, number | null> = {};
  for (const id of ['5', '912', 'CD1.X8', 'CD1.X9', 'CD1.X11', 'CD1.X12', 'CD1.X13', 'CD1.X14', 'CD4.X10', 'HUD24']) {
    money[id] = paymentCents(fields[id]);
    if (id !== '912' && !blank(fields[id]) && money[id] === null) {
      fail('PAYMENT_DATA_CONFLICT', `Invalid monetary value in ${id}.`, [id]);
    }
  }
  const required = ['5', 'CD1.X8', 'CD1.X9', 'CD1.X13', 'CD1.X14', 'CD4.X10'];
  const missing = required.filter(id => money[id] === null);
  if (missing.length) fail('PAYMENT_DATA_INCOMPLETE', 'Loan-specific payment fields are missing.', missing);
  const pi = money['5']!;
  const escrow = money['CD4.X10']!;
  const total = money['CD1.X13']!;
  const range = flag(fields['CD1.XD1']);
  if (range === null) fail('PAYMENT_DATA_INCOMPLETE', 'The disclosed payment range indicator is missing.', ['CD1.XD1']);
  if (range || money['CD1.X14'] !== total || money['CD1.X8'] !== money['CD1.X9']) {
    fail('PAYMENT_PERIOD_UNSUPPORTED', 'A ranged payment requires servicing review.', ['CD1.X13', 'CD1.X14', 'CD1.XD1']);
  }
  if (pi <= 0 || total <= 0 || money['CD1.X8'] !== pi || pi + escrow !== total) {
    fail('PAYMENT_DATA_CONFLICT', 'The Closing Disclosure payment does not reconcile with this loan\'s principal, interest and collected escrow.', ['5', 'CD1.X8', 'CD1.X13', 'CD4.X10']);
  }
  if (money.HUD24 !== null && money.HUD24 !== escrow) {
    fail('PAYMENT_DATA_CONFLICT', 'The escrow sources disagree.', ['CD4.X10', 'HUD24']);
  }
  if (money['CD1.X11'] !== null && money['CD1.X12'] !== null && money['CD1.X11']! + money['CD1.X12']! !== escrow) {
    fail('PAYMENT_DATA_CONFLICT', 'Disclosed escrow and mortgage insurance do not reconcile.', ['CD1.X11', 'CD1.X12', 'CD4.X10']);
  }
  const lienText = String(fields['420'] ?? '').replace(/\s/g, '').toLowerCase();
  const lien = ['first', 'firstlien'].includes(lienText) ? 'FirstLien' : ['second', 'secondlien'].includes(lienText) ? 'SecondLien' : null;
  if (!lien) fail('PAYMENT_DATA_INCOMPLETE', 'Lien position must identify the loan being transferred.', ['420']);
  const amortization = String(fields['608'] ?? '').replace(/\s/g, '').toLowerCase();
  const temporaryBuydown = String(fields['4645'] ?? '').trim();
  const buydown = flag(fields['425']);
  if (!amortization) fail('PAYMENT_DATA_INCOMPLETE', 'Amortization type is missing.', ['608']);
  if (!blank(fields['425']) && buydown === null) {
    fail('PAYMENT_DATA_CONFLICT', 'The buydown indicator is invalid.', ['425']);
  }
  if (!['fixed', 'fixedrate', 'adjustable', 'adjustablerate'].includes(amortization) || buydown === true ||
    (temporaryBuydown !== '' && !['no', 'none', 'n', 'false'].includes(temporaryBuydown.toLowerCase()))) {
    fail('PAYMENT_PERIOD_UNSUPPORTED', 'This payment structure requires servicing review.', ['608', '425', '4645']);
  }
  const first = paymentDate(fields['682']);
  if (!first) fail('PAYMENT_DATA_INCOMPLETE', 'The Encompass first payment date is missing.', ['682']);
  if (first !== firstPaymentDate || !paymentDate(dueDate)) {
    fail('PAYMENT_DATA_CONFLICT', 'The servicing and Encompass payment dates disagree.', ['682', 'first_payment_date']);
  }
  const years = Number(fields['CD1.X7']);
  if (!['string', 'number'].includes(typeof fields['CD1.X7']) || blank(fields['CD1.X7']) || !Number.isInteger(years) || years < 1 || years > 50) {
    fail('PAYMENT_DATA_INCOMPLETE', 'The initial disclosed payment period is missing or invalid.', ['CD1.X7']);
  }
  const months = (Number(dueDate.slice(0, 4)) - Number(first!.slice(0, 4))) * 12 + Number(dueDate.slice(5, 7)) - Number(first!.slice(5, 7));
  if (months < 0 || months >= years * 12 || dueDate.slice(8) !== first!.slice(8)) {
    fail('PAYMENT_PERIOD_UNSUPPORTED', 'The requested payment is outside the initial disclosed period.', ['CD1.X7', '682']);
  }
  if (scheduleAmount !== undefined && paymentCents(scheduleAmount) !== total) {
    fail('PAYMENT_DATA_CONFLICT', 'The servicing schedule disagrees with the validated loan payment.', ['servicing_payments.total_amount_due', 'CD1.X13']);
  }
  return {
    version: GOODBYE_PAYMENT_VERSION, source: 'CD1.X13', amountCents: total,
    principalAndInterestCents: pi, collectedEscrowCents: escrow,
    firstPaymentDate: first!, dueDate, initialPeriodYears: years, lienPosition: lien!,
    fields: { ...money, '420': lien!, '425': flag(fields['425']) ?? false, '4645': temporaryBuydown.toLowerCase(), '608': amortization,
      '682': first, 'CD1.X7': years, 'CD1.XD1': range }
  };
}
